import type { ViewID } from '@src/config';
import type { SeriesClassificationResult } from '@src/store/dicom-web/SeriesClassification';

import { InitViewIDs } from '@src/config';
import {
  AnalysisType,
  Orientation,
} from '@src/store/dicom-web/classification-typing';
import { seriesClassification } from '@src/store/dicom-web/SeriesClassification';

import { useCurrentStudyStore } from '../dicom-web/current-study-store';

/**
 * Rule primitives. Each factory returns a {@link SeriesRule} whose `validate`
 * runs against a {@link SeriesDescriptor}. A failing rule blocks the selection
 * (hard reject) and its `reason` is surfaced to the user.
 */
export const Rules = {
  /** Constrains the number of slices in the series. */
  slices(constraint: CountConstraint): SeriesRule {
    return {
      id: 'slices',
      validate: d =>
        satisfiesCount(constraint, d.numberOfSlices)
          ? { ok: true }
          : { ok: false, reason: describeCount('Slice count', constraint, d.numberOfSlices) },
    };
  },

  /** Constrains the number of phases (max images per slice) in the series. */
  phases(constraint: CountConstraint): SeriesRule {
    return {
      id: 'phases',
      validate: d =>
        satisfiesCount(constraint, d.maxPhases)
          ? { ok: true }
          : { ok: false, reason: describeCount('Phase count', constraint, d.maxPhases) },
    };
  },

  /** Requires the series classification to be one of the given analysis types. */
  requireClassification(types: AnalysisType[]): SeriesRule {
    return {
      id: 'classification',
      validate: (d) => {
        const type = d.classification?.analysisType;
        if (type && types.includes(type)) return { ok: true };
        return {
          ok: false,
          reason: `Series classification must be ${types.join(' or ')} (is ${type ?? 'unknown'}).`,
        };
      },
    };
  },

  /** Requires the series orientation to be one of the given orientations. */
  requireOrientation(orientations: Orientation[]): SeriesRule {
    return {
      id: 'orientation',
      validate: (d) => {
        const orientation = d.orientation;
        if (orientation && orientations.includes(orientation)) return { ok: true };
        return {
          ok: false,
          reason: `Series orientation must be ${orientations.join(' or ')} (is ${orientation ?? 'unknown'}).`,
        };
      },
    };
  },

  /** Requires a venc image to be present (or absent) as the first image of the series. */
  requireVenc(present: boolean): SeriesRule {
    return {
      id: 'venc',
      validate: d =>
        d.hasVencImage === present
          ? { ok: true }
          : {
              ok: false,
              reason: present
                ? 'A venc (phase-contrast) image is required for this viewport.'
                : 'A venc (phase-contrast) image is not allowed in this viewport.',
            },
    };
  },

  /** Requires the series to be a parallel slice stack. */
  requireParallelStack(): SeriesRule {
    return {
      id: 'parallelStack',
      validate: d =>
        d.parallelStack
          ? { ok: true }
          : { ok: false, reason: 'Series must be a parallel slice stack.' },
    };
  },

  /** Requires every slice to contain the same number of phases. */
  uniformPhases(): SeriesRule {
    return {
      id: 'uniformPhases',
      validate: d =>
        d.uniformPhases
          ? { ok: true }
          : { ok: false, reason: 'Every slice must have the same number of phases.' },
    };
  },
};

/** All long-axis orientations (2/3/4/5-chamber views). */
// const LAX_ORIENTATIONS = [
//   Orientation.Lax2CV,
//   Orientation.Lax3CV,
//   Orientation.Lax4CV,
//   Orientation.Lax5CV,
// ];

/**
 * Per-viewport series requirements. Every `InitViewIDs` entry is listed
 * explicitly so the coverage is exhaustive: an empty array means the viewport
 * accepts any series (no restrictions). Tune the concrete thresholds to match
 * the clinical expectations of each viewport.
 *
 * NOTE: a viewport omitted from this table is also treated as unrestricted, but
 * every known view is listed here on purpose so nothing is silently missed.
 */
export const ViewportSeriesRules: Partial<Record<ViewID, SeriesRule[]>> = {
  // --- OCG contouring (BOLD, 2-slice rest/stress stack) ---
  [InitViewIDs.ContoursLeft]: [
    // Rules.requireOrientation([Orientation.Sax, ...LAX_ORIENTATIONS]),
    // Rules.slices({ exact: 2 }),
  ],
  [InitViewIDs.ContoursRight]: [
    // Rules.requireOrientation([Orientation.Sax, ...LAX_ORIENTATIONS]),
    // Rules.slices({ exact: 2 }),
  ],

  // --- OCG analysis (mirrors the contouring views) ---
  [InitViewIDs.AnalysisLeft]: [
    // Rules.requireOrientation([Orientation.Sax, ...LAX_ORIENTATIONS]),
    // Rules.slices({ exact: 2 }),
  ],
  [InitViewIDs.AnalysisRight]: [
    // Rules.requireOrientation([Orientation.Sax, ...LAX_ORIENTATIONS]),
    // Rules.slices({ exact: 2 }),
  ],
  // Graphical-analysis helper view: no series restriction (placeholder).
  [InitViewIDs.AnalysisMiddle]: [],
  // General-purpose analysis view: any series allowed (placeholder).
  [InitViewIDs.AnalysisGeneralView]: [],

  // --- Function (parallel SAX cine stack) ---
  [InitViewIDs.FunctionMainView]: [
    // Rules.requireParallelStack(),
    // Rules.requireOrientation([Orientation.Sax]),
  ],
  [InitViewIDs.FunctionSecondaryView]: [
    //
  ],

  // --- Flow (phase-contrast / venc) ---
  [InitViewIDs.FlowMagnitude]: [
    Rules.requireVenc(true),
    Rules.slices({ exact: 2 }),
  ],
  [InitViewIDs.FlowPhase]: [
    Rules.requireVenc(true),
    Rules.slices({ exact: 2 }),
  ],

  // --- Generic viewer grid: any series allowed (placeholders) ---
  [InitViewIDs.ViewerComponentTopLeft]: [],
  [InitViewIDs.ViewerComponentTopRight]: [],
  [InitViewIDs.ViewerComponentBottomLeft]: [],
  [InitViewIDs.ViewerComponentBottomRight]: [],
};

/**
 * Everything the series rules need about a candidate series, gathered once so
 * individual rules do not each re-walk the study. Built from the stores by
 * {@link buildSeriesDescriptor}, but the rule primitives themselves operate on
 * this plain object so they stay pure and unit-testable.
 */
export interface SeriesDescriptor {
  seriesIdx: number;
  /** Number of slices in the series. */
  numberOfSlices: number;
  /** Image (phase) count for each slice, indexed by slice. */
  phasesPerSlice: number[];
  /** Largest per-slice image (phase) count. */
  maxPhases: number;
  /** True when every slice has the same number of phases (and there is at least one slice). */
  uniformPhases: boolean;
  /** True when the first image of the series is a 2D venc image. */
  hasVencImage: boolean;
  /** Classification of the series (resolved from its first image), if available. */
  classification?: SeriesClassificationResult;
  /** Convenience mirror of {@link classification}'s orientation. */
  orientation?: Orientation;
  /** Convenience mirror of {@link classification}'s parallelStack flag. */
  parallelStack: boolean;
}

export interface RuleResult {
  ok: boolean;
  /** Human readable explanation, present when `ok` is false. */
  reason?: string;
}

export interface SeriesRule {
  id: string;
  validate: (descriptor: SeriesDescriptor) => RuleResult;
}

interface CountConstraint {
  min?: number;
  max?: number;
  exact?: number;
}

function describeCount(what: string, c: CountConstraint, actual: number): string {
  if (c.exact !== undefined) return `${what} must be exactly ${c.exact} (has ${actual}).`;
  if (c.min !== undefined && c.max !== undefined)
    return `${what} must be between ${c.min} and ${c.max} (has ${actual}).`;
  if (c.min !== undefined) return `${what} must be at least ${c.min} (has ${actual}).`;
  if (c.max !== undefined) return `${what} must be at most ${c.max} (has ${actual}).`;
  return `${what} is invalid (has ${actual}).`;
}

function satisfiesCount(c: CountConstraint, actual: number): boolean {
  if (c.exact !== undefined && actual !== c.exact) return false;
  if (c.min !== undefined && actual < c.min) return false;
  if (c.max !== undefined && actual > c.max) return false;
  return true;
}

/**
 * Gathers everything the rules need for a candidate series. Safe to call with
 * any `seriesIdx`: out-of-range indices yield an empty descriptor.
 */
export function buildSeriesDescriptor(seriesIdx: number): SeriesDescriptor {
  const study = useCurrentStudyStore();

  const numberOfSlices = study.getSliceCount(seriesIdx);
  const phasesPerSlice = Array.from({ length: numberOfSlices }, (_, sliceIdx) => study.getImagesCount(seriesIdx, sliceIdx));
  const maxPhases = phasesPerSlice.length ? Math.max(...phasesPerSlice) : 0;
  const uniformPhases
    = phasesPerSlice.length > 0
      && phasesPerSlice.every(count => count === phasesPerSlice[0]);

  // venc is determined by the FIRST image of the series
  const firstImageUID = study.getImageUID({ seriesIdx, sliceIdx: 0, imageIdx: 0 });
  const hasVencImage
    = !!firstImageUID && study.imageInfoDicom?.[firstImageUID]?.IsVencImage2D === true;

  // classification is resolved from the candidate series, not the viewport,
  // because the series is not selected in the viewport yet
  const classification = firstImageUID
    ? seriesClassification.getClassificationBySopInstanceUID(firstImageUID)
    : undefined;

  return {
    seriesIdx,
    numberOfSlices,
    phasesPerSlice,
    maxPhases,
    uniformPhases,
    hasVencImage,
    classification,
    orientation: classification?.orientation,
    parallelStack: classification?.parallelStack ?? false,
  };
}

/** Runs the given rules against a descriptor. Pure — used by tests. */
export function runSeriesRules(
  descriptor: SeriesDescriptor,
  rules: SeriesRule[],
): RuleResult[] {
  return rules.map(rule => rule.validate(descriptor));
}

/**
 * Validates a candidate series for a viewport. Returns the results of every
 * applicable rule (empty array when the viewport has no restrictions). Callers
 * should reject the selection when any result has `ok === false`.
 */
export function validateSeriesForViewport(
  viewportId: string,
  seriesIdx: number,
): RuleResult[] {
  const rules = ViewportSeriesRules[viewportId];
  if (!rules || rules.length === 0) return [];

  const descriptor = buildSeriesDescriptor(seriesIdx);
  return runSeriesRules(descriptor, rules);
}

/**
 * Convenience wrapper returning only the failing rule results for a candidate
 * series in a viewport. An empty array means the series is valid.
 */
export function getSeriesRuleViolations(
  viewportId: string,
  seriesIdx: number,
): RuleResult[] {
  return validateSeriesForViewport(viewportId, seriesIdx).filter(r => !r.ok);
}
